/* eslint-disable no-underscore-dangle */
import { CONTENT_META } from '@/constants/linkContent';
import {
  LINK_META_CATEGORY,
  LINK_TYPE,
  WidgetTypes,
  WIDGET_META,
} from '@/constants/linkWidget';
import { prepareGetUpgradeOptions } from './features';


class WidgetMeta {
  /**
   * @param {LINK_META_CATEGORY} metaCategory
   * @param {Object} options
   * @param {WidgetTypes} options.type
   * @param {LinkCategories} options.category
   */
  constructor(metaCategory, { type, category = '' }) {
    this.type = type;
    this.category = category;
    this.metaCategory = metaCategory;
    this.widgetMeta = this._getMeta();
  }

  _getMeta() {
    const widgetMeta = WIDGET_META[`${this.type}-${this.category}`];
    return widgetMeta;
  }

  getTitle() {
    const { title } = this.widgetMeta;
    if (typeof title === 'string') {
      return title;
    }
    return title[this.metaCategory] || '';
  }

  getIcon() {
    const { icon } = this.widgetMeta;
    if (typeof icon === 'string') {
      return icon;
    }
    return icon[this.metaCategory] || '';
  }

  get() {
    return {
      ...this.widgetMeta,
      icon: this.getIcon(),
      title: this.getTitle(),
    };
  }
}

export class ContentMeta extends WidgetMeta {
  /**
   * @param {LINK_META_CATEGORY} metaCategory
   * @param {Object} options
   * @param {WidgetTypes} options.type
   */
  constructor(metaCategory, { type }) {
    super(metaCategory, { type });
  }

  // eslint-disable-next-line no-underscore-dangle
  _getMeta() {
    const contentMeta = CONTENT_META[`${this.type}-`];
    return contentMeta;
  }
}

/**
 * Return recommend link widget meta
 * @param {WidgetTypes} type
 * @param {LinkCategories} category
 * @returns {Object}
 */
function getRecommendWidgetMeta(type, category) {
  const recommendMeta = new WidgetMeta(LINK_META_CATEGORY.RECOMMEND, { type, category });
  return recommendMeta.get();
}

/**
 * Return default link widget meta
 * @param {WidgetTypes} type
 * @param {LinkCategories} category
 * @returns {Object}
 */
function getDefaultWidgetMeta(type, category) {
  const defaultMeta = new WidgetMeta(LINK_META_CATEGORY.DEFAULT, { type, category });
  return defaultMeta.get();
}

/**
 * Return recommend link content meta
 * @param {WidgetTypes} type
 * @returns {Object}
 */
export function getRecommendContentMeta(type) {
  const recommendMeta = new ContentMeta(LINK_META_CATEGORY.RECOMMEND, { type });
  return recommendMeta.get();
}

/**
 * Return default content meta
 * @param {WidgetTypes} type
 * @returns {Object}
 */
export function getDefaultContentMeta(type) {
  const defaultMeta = new ContentMeta(LINK_META_CATEGORY.DEFAULT, { type });
  return defaultMeta.get();
}

/**
 * @param {Object} widget
 * @param {Object} widget.data
 * @param {string} widget.linkType
 * @param {Object} userFeatures list of available user's features
 * @returns
 */
function getIsNeedUpgradeToUseWidget(widget, userFeatures) {
  const { data, linkType } = widget;
  let meta = {};

  try {
    switch (linkType) {
      case LINK_TYPE.WIDGET:
        meta = getDefaultWidgetMeta(data.type, data.category);
        break;
      case LINK_TYPE.CONTENT:
        meta = getDefaultContentMeta(data.type);
        break;
      default:
        // TODO: send an error
        return false;
    }
  } catch {
    return false;
  }

  const upgradeOptions = prepareGetUpgradeOptions(userFeatures)(meta.featureName);

  return upgradeOptions.isNeedUpgrade;
}

const REQUIRE_STRIPE_WIDGET_TYPES = [
  WidgetTypes.SUPPORT,
];

function getIsNeedStripeToUseWidget(widget, userStripeAccount) {
  const isStripeAccountActive = userStripeAccount && userStripeAccount.payouts_enabled;

  if (isStripeAccountActive) {
    return false;
  }

  const { data, linkType } = widget;

  try {
    switch (linkType) {
      case LINK_TYPE.WIDGET:
        return REQUIRE_STRIPE_WIDGET_TYPES.includes(data.type);
      default:
        return false;
    }
  } catch {
    return false;
  }
}

export {
  WidgetMeta,
  getRecommendWidgetMeta,
  getDefaultWidgetMeta,
  getIsNeedUpgradeToUseWidget,
  getIsNeedStripeToUseWidget,
};
